// app/lib/tracking/consent.ts
import apiService from "../api/api.service";
import {
  type ConsentCategories,
  type StoredConsent,
  type ConsentApiResponse,
  DEFAULT_CONSENT,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
} from "./types";

// Custom event — consent değiştiğinde subscribe edenler haber alsın
export const CONSENT_CHANGED_EVENT = "consent-changed";

/**
 * localStorage'dan consent'i okur. Yoksa veya versiyon eskimişse null döner.
 */
export const getStoredConsent = (): ConsentCategories | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: StoredConsent = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed.categories;
  } catch {
    return null;
  }
};

/**
 * Kullanıcının onay verip vermediğini döner (banner göstermek için).
 */
export const hasConsentDecision = (): boolean => {
  return getStoredConsent() !== null;
};

/**
 * Consent'i kaydet, Google Consent Mode'a sinyal gönder, custom event dispatch et.
 */
export const saveConsent = (categories: ConsentCategories): void => {
  if (typeof window === "undefined") return;

  const data: StoredConsent = {
    version: CONSENT_VERSION,
    timestamp: Date.now(),
    categories,
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(data));

  // Google Consent Mode'a güncelleme sinyali
  updateGoogleConsent(categories);

  // Facebook Pixel consent
  updateFacebookConsent(categories);

  // Custom event — TrackingScripts dinler, Facebook/TikTok'u koşullu yükler
  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGED_EVENT, { detail: categories }),
  );

  // Backend'e yasal kanıt için kaydet — hata olsa da kullanıcı akışı bozulmaz
  apiService
    .post("/consent", { categories, version: CONSENT_VERSION })
    .catch(() => {});
};

/**
 * Backend'deki consent kaydını çeker (login sonrası cross-device sync için).
 * Kayıt yoksa veya istek başarısız olursa null döner.
 */
export const fetchServerConsent = async (): Promise<ConsentCategories | null> => {
  try {
    const res = await apiService.get<ConsentApiResponse>("/consent");
    return res.data?.categories ?? null;
  } catch {
    return null;
  }
};

/**
 * Login sonrası çağrılır: backend'de kayıtlı consent varsa localStorage'a
 * ve Consent Mode'a uygular (tekrar backend'e POST etmez — zaten oradan geldi).
 */
export const syncConsentFromServer = async (): Promise<void> => {
  if (typeof window === "undefined") return;

  const serverConsent = await fetchServerConsent();
  if (!serverConsent) return;

  const data: StoredConsent = {
    version: CONSENT_VERSION,
    timestamp: Date.now(),
    categories: serverConsent,
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(data));

  updateGoogleConsent(serverConsent);
  updateFacebookConsent(serverConsent);

  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGED_EVENT, { detail: serverConsent }),
  );
};

/**
 * Onayı sıfırla (footer'daki "Çerez Tercihleri" butonu için).
 */
export const resetConsent = (): void => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(CONSENT_STORAGE_KEY);
  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGED_EVENT, { detail: null }),
  );
};

const updateGoogleConsent = (c: ConsentCategories): void => {
  if (typeof window === "undefined" || typeof window.gtag !== "function")
    return;
  window.gtag("consent", "update", {
    analytics_storage: c.analytics ? "granted" : "denied",
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
    functionality_storage: c.functional ? "granted" : "denied",
    personalization_storage: c.functional ? "granted" : "denied",
  });
};

const updateFacebookConsent = (c: ConsentCategories): void => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  window.fbq("consent", c.marketing ? "grant" : "revoke");
};

// Re-exports (kullanım kolaylığı)
export { DEFAULT_CONSENT };
export type { ConsentCategories };
